import React from "react";
import { Box, Typography } from "@mui/material";

const PAGE_BG = "#ffffff";
const INK = "#111111";
const GRID_SIZE = 12;

export const TUTOR_PDF_FOOTER = "Generated using Puzzle Interact © 2026";

/** Printable area after page margin. Letter is shorter than A4. */
export const TUTOR_PAGE_FORMATS = {
    letter: { id: "letter", width: 739, height: 979, pageSize: "letter", margin: "0.4in" },
    a4: { id: "a4", width: 718, height: 1047, pageSize: "A4", margin: "10mm" },
};

export function tutorPrintPageStyle(formatId) {
    const spec = TUTOR_PAGE_FORMATS[formatId] || TUTOR_PAGE_FORMATS.letter;
    return `@page { size: ${spec.pageSize}; margin: ${spec.margin}; } @media print { .tutor-ws-sheet { background: #fff !important; color: #111 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; } .tutor-ws-sheet * { color: #111 !important; border-color: #333 !important; } }`;
}

function cellSizeFor(formatId) {
    const spec = TUTOR_PAGE_FORMATS[formatId] || TUTOR_PAGE_FORMATS.letter;
    const pad = 32;
    const chrome = 88 + 16 + 210 + 40 + 24;
    const byHeight = Math.floor((spec.height - pad - chrome) / GRID_SIZE);
    const byWidth = Math.floor((spec.width - pad) / GRID_SIZE);
    return Math.max(36, Math.min(byHeight, byWidth, 52));
}

function GridBoard({ grid, showLetters, cell }) {
    if (!Array.isArray(grid) || grid.length === 0) {
        return null;
    }
    const numberSize = Math.max(8, Math.round(cell * 0.28));
    const letterSize = Math.max(13, Math.round(cell * 0.46));
    return (
        <Box sx={{ display: "inline-flex", flexDirection: "column" }}>
            {grid.map((row, rowIndex) => (
                <Box key={rowIndex} sx={{ display: "flex" }}>
                    {(row || []).map((cellValue, colIndex) => {
                        if (!cellValue) {
                            return (
                                <Box
                                    key={colIndex}
                                    sx={{ width: cell, height: cell, boxSizing: "border-box" }}
                                />
                            );
                        }
                        return (
                            <Box
                                key={colIndex}
                                sx={{
                                    width: cell,
                                    height: cell,
                                    border: "1px solid currentColor",
                                    position: "relative",
                                    boxSizing: "border-box",
                                }}
                            >
                                {cellValue.number ? (
                                    <Box
                                        sx={{
                                            position: "absolute",
                                            top: 1,
                                            left: 2,
                                            fontSize: numberSize,
                                            fontWeight: 700,
                                            lineHeight: 1,
                                            color: "inherit",
                                        }}
                                    >
                                        {cellValue.number}
                                    </Box>
                                ) : null}
                                {showLetters ? (
                                    <Box
                                        sx={{
                                            fontSize: letterSize,
                                            fontWeight: 700,
                                            textAlign: "center",
                                            lineHeight: `${cell}px`,
                                            color: "inherit",
                                        }}
                                    >
                                        {cellValue.letter}
                                    </Box>
                                ) : null}
                            </Box>
                        );
                    })}
                </Box>
            ))}
        </Box>
    );
}

function ClueColumn({ title, items }) {
    return (
        <Box sx={{ flex: 1, minWidth: 0, color: "inherit" }}>
            <Typography sx={{ fontWeight: 800, fontSize: 13, mb: 0.75, color: "inherit", letterSpacing: 0.4 }}>
                {title}
            </Typography>
            {(items || []).map((item) => (
                <Typography key={`${title}-${item.number}`} sx={{ fontSize: 11, mb: 0.65, color: "inherit", lineHeight: 1.35 }}>
                    {item.number}. {item.meaning || "—"}
                </Typography>
            ))}
        </Box>
    );
}

function WorksheetPage({
    title,
    orgName,
    logoUrl,
    pageFormat,
    pageBreakAfter,
    answersLabel,
    flow,
    children,
}) {
    const spec = TUTOR_PAGE_FORMATS[pageFormat] || TUTOR_PAGE_FORMATS.letter;
    return (
        <Box
            className={pageBreakAfter ? "tutor-ws-page tutor-ws-sheet" : "tutor-ws-sheet"}
            sx={{
                width: spec.width,
                height: flow ? "auto" : spec.height,
                minHeight: flow ? spec.height : undefined,
                bgcolor: PAGE_BG,
                color: INK,
                px: 2,
                py: 1.5,
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
            }}
        >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
                {logoUrl ? (
                    <Box
                        component="img"
                        src={logoUrl}
                        alt=""
                        sx={{ width: 40, height: 40, objectFit: "contain", flexShrink: 0 }}
                    />
                ) : null}
                <Box sx={{ flex: 1, minWidth: 0, textAlign: "center" }}>
                    <Typography
                        sx={{
                            fontWeight: 800,
                            fontSize: 22,
                            color: "inherit",
                            letterSpacing: 0.6,
                            textTransform: "uppercase",
                            lineHeight: 1.2,
                        }}
                    >
                        {title || orgName || "Crossword"}
                    </Typography>
                    {orgName && title && orgName !== title ? (
                        <Typography sx={{ fontSize: 12, color: "inherit", mt: 0.25 }}>
                            {orgName}
                        </Typography>
                    ) : null}
                </Box>
                {logoUrl ? <Box sx={{ width: 40, flexShrink: 0 }} /> : null}
            </Box>
            <Box sx={{ borderBottom: "2px solid currentColor", mb: 1.5 }} />
            {answersLabel ? (
                <Typography sx={{ fontWeight: 800, fontSize: 13, color: "inherit", mb: 1, letterSpacing: 0.4 }}>
                    {answersLabel}
                </Typography>
            ) : null}
            <Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box>
            <Typography
                sx={{
                    mt: "auto",
                    pt: 1,
                    textAlign: "center",
                    fontSize: 11,
                    color: "inherit",
                    fontWeight: 600,
                }}
            >
                {TUTOR_PDF_FOOTER}
            </Typography>
        </Box>
    );
}

export default function TutorsWorksheetPrint({
    title,
    orgName,
    logoUrl,
    puzzle,
    includeAnswerPage = true,
    pageFormat = "letter",
    sourceText = "",
}) {
    if (!puzzle?.grid?.length) {
        return null;
    }
    const cell = cellSizeFor(pageFormat);
    const original = String(sourceText || "").trim();
    const hasOriginal = original.length > 0;
    return (
        <Box>
            <style>
                {`
                @media print {
                    .tutor-ws-page { page-break-after: always; break-after: page; }
                }
                `}
            </style>
            <WorksheetPage
                title={title}
                orgName={orgName}
                logoUrl={logoUrl}
                pageFormat={pageFormat}
                pageBreakAfter={includeAnswerPage || hasOriginal}
            >
                <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
                    <GridBoard grid={puzzle.grid} showLetters={false} cell={cell} />
                </Box>
                <Box sx={{ display: "flex", gap: 3, alignItems: "flex-start" }}>
                    <ClueColumn title="DOWN" items={puzzle.down} />
                    <ClueColumn title="ACROSS" items={puzzle.across} />
                </Box>
            </WorksheetPage>
            {includeAnswerPage ? (
                <WorksheetPage
                    title={title}
                    orgName={orgName}
                    logoUrl={logoUrl}
                    pageFormat={pageFormat}
                    pageBreakAfter={hasOriginal}
                    answersLabel="Answers"
                >
                    <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
                        <GridBoard grid={puzzle.grid} showLetters cell={cell} />
                    </Box>
                    {(puzzle.answers || []).map((item) => (
                        <Typography key={`${item.number}-${item.word}`} sx={{ fontSize: 12, mb: 0.5, color: "inherit" }}>
                            {item.word} — {item.meaning || "—"}
                        </Typography>
                    ))}
                </WorksheetPage>
            ) : null}
            {hasOriginal ? (
                <WorksheetPage
                    title={title}
                    orgName={orgName}
                    logoUrl={logoUrl}
                    pageFormat={pageFormat}
                    answersLabel="Original content"
                    flow
                >
                    <Typography sx={{ fontSize: 12, lineHeight: 1.55, whiteSpace: "pre-wrap", color: "inherit" }}>
                        {original}
                    </Typography>
                </WorksheetPage>
            ) : null}
        </Box>
    );
}
